import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Save, Crown, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/** Editor de Planes para Super Admin.
 * Permite modificar precio promo, precio regular, meses de promo, features,
 * descripcion, CTA y flag destacado. Todo se guarda en la coleccion
 * `planes_catalogo` via PUT /api/admin/planes/{id}.
 */
export default function AdminPlanesEditor() {
  const [planes, setPlanes] = useState([]);
  const [features, setFeatures] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardandoId, setGuardandoId] = useState('');
  const [edits, setEdits] = useState({}); // { plan_id: {campo: valor} }

  const cargar = () => {
    setCargando(true);
    api.get('/admin/planes')
      .then(r => { setPlanes(r.data.planes); setFeatures(r.data.features_catalogo); })
      .catch(() => toast.error('Error cargando planes'))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const valor = (plan, campo) => {
    if (edits[plan.id] && campo in edits[plan.id]) return edits[plan.id][campo];
    return plan[campo];
  };

  const setValor = (planId, campo, val) => {
    setEdits(prev => ({ ...prev, [planId]: { ...(prev[planId] || {}), [campo]: val } }));
  };

  const toggleFeature = (plan, fk) => {
    const current = valor(plan, 'features') || [];
    const nuevos = current.includes(fk) ? current.filter(f => f !== fk) : [...current, fk];
    setValor(plan.id, 'features', nuevos);
  };

  const guardar = async (planId) => {
    if (!edits[planId]) return;
    setGuardandoId(planId);
    try {
      await api.put(`/admin/planes/${planId}`, edits[planId]);
      toast.success('Plan actualizado');
      setEdits(prev => { const n = { ...prev }; delete n[planId]; return n; });
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setGuardandoId(''); }
  };

  if (cargando) return <p className="text-sm text-slate-500">Cargando planes...</p>;

  return (
    <div className="space-y-6" data-testid="admin-planes-editor">
      <div className="bg-cyan-500/10 border border-cyan-800 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-cyan-300">Editor de Planes de Suscripcion</p>
            <p className="text-xs text-slate-400 mt-1">
              Modifica precios, meses de promoción, features incluidas y CTA de cada plan. Los cambios afectan
              inmediatamente la pantalla pública <span className="font-mono text-cyan-400">/planes</span> y el
              proceso de suscripción de nuevos clientes.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {planes.map(plan => {
          const tieneCambios = !!edits[plan.id];
          const featsSel = valor(plan, 'features') || [];
          return (
            <Card key={plan.id} className={`bg-slate-900/40 ${tieneCambios ? 'border-amber-600' : 'border-slate-800'} border-2`}
              data-testid={`plan-editor-${plan.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-barlow text-lg text-slate-100 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-400" /> {valor(plan, 'nombre')}
                  </CardTitle>
                  {valor(plan, 'destacado') && (
                    <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-800 text-[10px]">Destacado</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Nombre</Label>
                  <Input value={valor(plan, 'nombre') || ''} onChange={e => setValor(plan.id, 'nombre', e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-200" data-testid={`plan-${plan.id}-nombre`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Descripcion</Label>
                  <Textarea value={valor(plan, 'descripcion') || ''} onChange={e => setValor(plan.id, 'descripcion', e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-200 text-xs" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Precio promo</Label>
                    <Input type="number" step="1" value={valor(plan, 'precio_promo') ?? ''}
                      onChange={e => setValor(plan.id, 'precio_promo', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                      data-testid={`plan-${plan.id}-precio-promo`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Precio regular</Label>
                    <Input type="number" step="1" value={valor(plan, 'precio_regular') ?? ''}
                      onChange={e => setValor(plan.id, 'precio_regular', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                      data-testid={`plan-${plan.id}-precio-regular`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Meses de promo</Label>
                    <Input type="number" step="1" min="0" max="24"
                      value={valor(plan, 'meses_promo') ?? ''}
                      onChange={e => setValor(plan.id, 'meses_promo', parseInt(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-700 text-slate-200 font-mono" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Moneda</Label>
                    <select value={valor(plan, 'moneda') || 'MXN'}
                      onChange={e => setValor(plan.id, 'moneda', e.target.value)}
                      className="w-full h-9 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-2 text-sm">
                      <option value="MXN">MXN</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Promo visible para</Label>
                  <select value={valor(plan, 'promo_visible_para') || 'solo_nuevos'}
                    onChange={e => setValor(plan.id, 'promo_visible_para', e.target.value)}
                    className="w-full h-9 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-2 text-sm"
                    data-testid={`plan-${plan.id}-promo-visible-para`}>
                    <option value="solo_nuevos">Solo nuevos usuarios (nunca han pagado)</option>
                    <option value="todos">Todos los usuarios (incluyendo pagantes)</option>
                    <option value="nadie">Nadie (siempre precio regular)</option>
                  </select>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Los usuarios que ya pagaron una suscripción verán el precio regular cuando la promo esté restringida.
                  </p>
                </div>
                {plan.id === 'multisucursal' && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Precio por sucursal extra</Label>
                    <Input type="number" step="1" value={valor(plan, 'precio_por_sucursal_extra') ?? ''}
                      onChange={e => setValor(plan.id, 'precio_por_sucursal_extra', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-700 text-slate-200 font-mono" />
                  </div>
                )}
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">CTA (Call to action)</Label>
                  <Input value={valor(plan, 'cta') || ''} onChange={e => setValor(plan.id, 'cta', e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-200" placeholder="Ej: EL MAS POPULAR" />
                </div>

                <div className="flex items-center gap-4 pt-1">
                  <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={valor(plan, 'destacado') || false}
                      onChange={e => setValor(plan.id, 'destacado', e.target.checked)}
                      className="accent-cyan-500" /> Destacado
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={valor(plan, 'activo') !== false}
                      onChange={e => setValor(plan.id, 'activo', e.target.checked)}
                      className="accent-emerald-500" /> Activo (visible)
                  </label>
                </div>

                <div className="pt-2 border-t border-slate-800">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                    Features incluidas ({featsSel.length}/{Object.keys(features).length})
                  </p>
                  <div className="grid grid-cols-1 gap-1 max-h-64 overflow-y-auto pr-1">
                    {Object.entries(features).map(([fk, label]) => (
                      <label key={fk} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer hover:bg-slate-800/40 px-2 py-1 rounded">
                        <input type="checkbox" checked={featsSel.includes(fk)}
                          onChange={() => toggleFeature(plan, fk)}
                          className="accent-cyan-500" data-testid={`plan-${plan.id}-feat-${fk}`} />
                        {featsSel.includes(fk) && <Check className="w-3 h-3 text-cyan-400" />}
                        <span className="truncate">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <Button onClick={() => guardar(plan.id)}
                  disabled={!tieneCambios || guardandoId === plan.id}
                  className={`w-full font-bold ${tieneCambios ? 'bg-amber-500 hover:bg-amber-600 text-black' : 'bg-slate-800 text-slate-600'}`}
                  data-testid={`guardar-plan-${plan.id}-btn`}>
                  <Save className="w-4 h-4 mr-2" />
                  {guardandoId === plan.id ? 'Guardando...' : tieneCambios ? 'Guardar cambios' : 'Sin cambios'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
